import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { api, formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Mountain, Loader2, ShieldCheck, ArrowLeft, MailCheck } from 'lucide-react';

// Pagina PUBBLICA: verifica dell'indirizzo email tramite codice OTP ricevuto via email.
export default function VerifyEmail() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { verifyEmail, resendEmailOtp } = useAuth();
  const email = (params.get('email') || '').toLowerCase();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const data = await verifyEmail(email, code);
      setDone(true);
      toast.success('Email verificata');
      if (data.role === 'customer' && data.access_token) {
        setTimeout(() => navigate('/cliente'), 1200);
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || 'Codice non valido');
    } finally { setLoading(false); }
  };

  const resend = async () => {
    try { await resendEmailOtp(email); toast.success('Ti abbiamo inviato un nuovo codice'); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || 'Invio non riuscito'); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6">
      <div className="w-full max-w-md">
        <Link to="/" className="mb-6 inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground" data-testid="verify-email-home-link"><ArrowLeft className="h-4 w-4" />Torna alla Home</Link>
        <div className="mb-8 flex items-center gap-2.5">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground"><Mountain className="h-5 w-5" /></div>
          <div><p className="font-display text-lg font-bold">Sila Experiences</p><p className="text-xs text-muted-foreground">Verifica email</p></div>
        </div>

        {!email ? (
          <div data-testid="verify-email-no-email">
            <h1 className="font-display text-2xl font-bold tracking-tight">Verifica non disponibile</h1>
            <p className="mt-2 text-sm text-muted-foreground">Apri la pagina dal link corretto o registrati di nuovo.</p>
            <p className="mt-6 text-sm"><Link to="/login" className="inline-flex items-center font-medium text-primary hover:underline"><ArrowLeft className="mr-1.5 h-4 w-4" />Torna all'accesso</Link></p>
          </div>
        ) : done ? (
          <div data-testid="verify-email-done">
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-accent"><ShieldCheck className="h-6 w-6 text-primary" /></div>
            <h1 className="font-display text-2xl font-bold tracking-tight">Email verificata</h1>
            <p className="mt-2 text-sm text-muted-foreground">Grazie! Il tuo indirizzo è stato verificato. Se sei un partner, il tuo account resta in attesa di approvazione dell'amministratore.</p>
            <p className="mt-6 text-sm"><Link to="/login" className="inline-flex items-center font-medium text-primary hover:underline"><ArrowLeft className="mr-1.5 h-4 w-4" />Vai all'accesso</Link></p>
          </div>
        ) : (
          <>
            <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-accent"><MailCheck className="h-6 w-6 text-primary" /></div>
            <h1 className="font-display text-2xl font-bold tracking-tight">Verifica il tuo indirizzo email</h1>
            <p className="mt-1 text-sm text-muted-foreground">Abbiamo inviato un codice di 6 cifre a <strong>{email}</strong>. È valido 5 minuti.</p>
            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="verify-email-form">
              <div className="space-y-2">
                <Label htmlFor="code">Codice di verifica</Label>
                <Input id="code" inputMode="numeric" maxLength={6} required value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} placeholder="______" className="text-center text-lg tracking-[0.4em]" data-testid="verify-email-input" />
              </div>
              {error && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="verify-email-error">{error}</p>}
              <Button type="submit" className="w-full" disabled={loading || code.length !== 6} data-testid="verify-email-submit-button">{loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Verifica</Button>
            </form>
            <button type="button" onClick={resend} className="mt-4 text-sm font-medium text-primary hover:underline" data-testid="verify-email-resend-button">Invia di nuovo il codice</button>
          </>
        )}
      </div>
    </div>
  );
}
